import {
  AnySessionPart,
  SessionEvent,
  SessionEventKind,
  SessionPartKind,
} from "@/mds/components/sessions/session.types";

/**
 * @description
 * This utility prepares a summary of events, summary of the latest "interaction" or "sub-session".
 * There can ever be at most 1 summary (for the late finished sub-session).
 * It filters all session events and keeps only AGENT_TOOL_INVOCATION (with message and chip) within calculated timeframe boundaries.
 * These timeframe boundaries are defined as follows:
 * - The start is the timestamp of the latest user message sent if it was sent during the finished or nonexistent session.
 * - The end is defined by the latest session update timestamp if the session has finished status.
 */
export const generateSessionSummary = (events: SessionEvent[]) => {
  // Filter for AGENT_TOOL_INVOCATION events with message and chip
  const summaryEvents = events.filter(
    event => event.data.kind === SessionEventKind.AgentActionV1 && hasArtifactPart(event)
  );

  // Calculate timeframe boundaries
  const boundaries = calculateTimeframeBoundaries(events);

  // Filter events within boundaries

  const filteredEvents = summaryEvents.filter(event => isWithinBoundaries(event, boundaries));

  return {
    filteredEvents,
    timeframeBoundaries: boundaries,
  };
};

const hasArtifactPart = (event: SessionEvent): boolean => {
  // Check if the event has both message and chip parts
  if (!event.data?.value?.parts) return false;

  const parts = event.data.value.parts as AnySessionPart[];
  const hasArtifact = parts.some(part => part.kind === SessionPartKind.ArtifactV1);

  return hasArtifact;
};

const calculateTimeframeBoundaries = (events: SessionEvent[]) => {
  // Start: Latest user message during finished/nonexistent session
  const latestUserMessage = events
    .filter(e => e.data.kind === SessionEventKind.UserActionV1)
    .sort(
      (a, b) =>
        new Date(b.internal_created_at).getTime() - new Date(a.internal_created_at).getTime()
    )[0];

  const endTimestamp = new Date().toISOString();

  return {
    start: latestUserMessage?.internal_created_at || new Date().toISOString(),
    end: endTimestamp,
  };
};

const isWithinBoundaries = (
  event: SessionEvent,
  boundaries: { start: string; end: string }
): boolean => {
  const eventTime = new Date(event.internal_created_at).getTime();
  const startTime = new Date(boundaries.start).getTime();
  const endTime = new Date(boundaries.end).getTime();

  return eventTime >= startTime && eventTime <= endTime;
};
